import React, { useEffect, useRef, useState } from 'react';

/* ─────────────────────────────────────────────────────────
   Reveal — fade + slide up when entering viewport
   ───────────────────────────────────────────────────────── */
export const Reveal = ({
  children,
  delay = 0,
  y = 28,
  duration = 900,
  className = '',
  as: Tag = 'div',
  threshold = 0.15,
  once = true,
}) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setSeen(false);
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [once, threshold]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: seen ? 1 : 0,
        transform: seen ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
};

/* ─────────────────────────────────────────────────────────
   SplitText — word-by-word rising reveal
   ───────────────────────────────────────────────────────── */
export const SplitText = ({ text, className = '', delay = 0, stagger = 70 }) => {
  return (
    <span className={className} aria-label={text}>
      {text.split(' ').map((word, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-bottom"
          style={{ paddingBottom: '0.06em' }}
          aria-hidden
        >
          <span
            className="inline-block"
            style={{
              animation: `wordRise 1100ms cubic-bezier(0.22, 1, 0.36, 1) ${delay + i * stagger}ms backwards`,
              willChange: 'transform, opacity',
            }}
          >
            {word}
            {i < text.split(' ').length - 1 && '\u00A0'}
          </span>
        </span>
      ))}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────
   Magnetic — subtle attraction toward cursor
   ───────────────────────────────────────────────────────── */
export const Magnetic = ({ children, strength = 0.22, className = '' }) => {
  const ref = useRef(null);

  const handleMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const r = node.getBoundingClientRect();
    const x = e.clientX - (r.left + r.width / 2);
    const y = e.clientY - (r.top + r.height / 2);
    node.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
  };

  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = '';
  };

  return (
    <span
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`inline-block transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform ${className}`}
    >
      {children}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────
   ScrollProgress — gold hairline at top
   ───────────────────────────────────────────────────────── */
export const ScrollProgress = () => {
  const [p, setP] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - window.innerHeight;
      const pct = total > 0 ? (window.scrollY / total) * 100 : 0;
      setP(pct);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="fixed top-0 inset-x-0 z-[60] h-[2px] pointer-events-none bg-transparent">
      <div
        className="h-full bg-gradient-to-r from-[#9A7F4F] via-[#C9A875] to-[#9A7F4F]"
        style={{
          width: `${p}%`,
          transition: 'width 90ms linear',
          boxShadow: '0 0 8px rgba(201,168,117,0.6)',
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────────────────
   useParallax — image scrolls slower than content
   ───────────────────────────────────────────────────────── */
export const useParallax = (speed = 0.18) => {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let raf;
    const update = () => {
      const r = node.getBoundingClientRect();
      const center = r.top + r.height / 2 - window.innerHeight / 2;
      const y = center * speed * -1;
      node.style.transform = `translate3d(0, ${y}px, 0) scale(1.1)`;
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [speed]);

  return ref;
};

/* ─────────────────────────────────────────────────────────
   AnimatedDivider — gold line draws left-to-right on view
   ───────────────────────────────────────────────────────── */
export const AnimatedDivider = ({ width = '3rem', color = '#9A7F4F', delay = 0 }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <span
      ref={ref}
      style={{
        display: 'inline-block',
        width: seen ? width : '0',
        height: '1px',
        background: color,
        transition: `width 1100ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    />
  );
};

/* Inject keyframes once */
if (typeof document !== 'undefined' && !document.getElementById('tarhun-fx-keyframes')) {
  const style = document.createElement('style');
  style.id = 'tarhun-fx-keyframes';
  style.innerHTML = `
    @keyframes wordRise {
      from { transform: translateY(110%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @keyframes shimmerLine {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
}
